'use client';
import React, { useMemo } from 'react';
import { Box, Stack, Typography, Divider, Button, useDecNiceModal } from '@castlery/fortress';
import { calcSummary } from './util';
import { accessInUS } from '@castlery/config';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectOrder } from '@castlery/modules-order-domain';
import { PromotionDetails } from './promotion-details';
import { RowStack } from './row-stack';
import { toPrice } from '@castlery/utils';
import { orderFeatureService } from '@castlery/modules-order-services';

interface OrderSummaryProps {
  inCheckout?: boolean;
  CouponWallet: React.ReactNode;
}
export const OrderSummary: React.FC<OrderSummaryProps> = ({ CouponWallet, inCheckout = false }) => {
  const { NiceModal, modalProps, toggleModal } = useDecNiceModal();

  const order = useAppSelector(selectOrder);
  const promotions = useMemo(() => order?.promotions || [], [order]);
  const totalAddonDescription = orderFeatureService.getOrderSummaryTotalAddonDescription(order?.state === 'cart');
  const showSalesTax = orderFeatureService.showSalesTax;

  const {
    total,
    itemTotal,
    additionalTaxTotal,
    serviceOriginal,
    serviceTotal,
    // servicePromo,
    shipmentTotal,
    shipmentOriginal,
    promoTotal,
  } = useMemo(() => calcSummary(order), [order]);
  const zeroToFree = !!(order && order.line_items.length > 0);
  const payableTotal = useMemo(() => {
    if (!order || !order?.total) return 0;
    return Number(order.total) - (order.payments?.reduce((acc, payment) => acc + Number(payment.amount), 0) || 0);
  }, [order]);

  const showWarningMessage = accessInUS && Array.isArray(order?.warning_messages) && order.warning_messages.length > 0;
  const errorMessage = order?.warning_messages?.join('');

  return (
    <React.Fragment>
      <Box sx={{ width: '100%' }}>
        {/* ========================== item total ============================ */}
        <RowStack>
          <Typography level="body2">Items Subtotal</Typography>
          <Typography level="body2">{toPrice(itemTotal, zeroToFree)}</Typography>
        </RowStack>
        <Divider />
        {/* ========================== warranty total ============================ */}
        {!!Number(order?.warranty_total) && (
          <>
            <RowStack>
              <Typography level="body2">Warranty Subtotal</Typography>
              <Typography level="body2">{toPrice(order.warranty_total)}</Typography>
            </RowStack>
            <Divider />
          </>
        )}
        {/* ========================== shipping fee ============================ */}
        <RowStack>
          <Typography level="body2">Shipping</Typography>
          {/* =============   显示原价 ============ */}
          <RowStack sx={{ padding: 0 }}>
            {shipmentOriginal !== shipmentTotal && !!Number(shipmentOriginal) && (
              <Typography level="body2">{toPrice(shipmentOriginal, false)}</Typography>
            )}
            <Typography level="body2">{toPrice(shipmentTotal, zeroToFree)}</Typography>
          </RowStack>
        </RowStack>
        <Divider />
        {/* ========================== services total ============================ */}
        {!!serviceOriginal && (
          <>
            <RowStack>
              <Typography level="body2">Services</Typography>
              <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                {serviceOriginal !== serviceTotal && (
                  <Typography
                    level="body2"
                    sx={{
                      textDecoration: 'line-through',
                    }}
                  >
                    {toPrice(serviceOriginal)}
                  </Typography>
                )}
                <Typography level="body2">{toPrice(serviceTotal, true)}</Typography>
              </Stack>
            </RowStack>
            <Divider />
          </>
        )}
        {/* ========================== promotion fee ============================ */}
        {!!promoTotal && (
          <>
            <RowStack>
              <Typography level="body2">
                Promotion
                <Button variant="tertiary" sx={{ padding: 0, minHeight: 24 }} onClick={toggleModal}>
                  <Typography level="caption1" color="neutral">
                    (View Details)
                  </Typography>
                </Button>
              </Typography>
              <Typography level="body2">{toPrice(promoTotal)}</Typography>
            </RowStack>
            <Divider />
          </>
        )}
        {/* ===================== Hint msg for order ======================== */}
        {showWarningMessage && (
          <>
            <Typography level="caption1" color="danger" sx={{ paddingY: 1, paddingX: 2 }}>
              {errorMessage}
            </Typography>
            <Divider />
          </>
        )}

        {CouponWallet}
        {/* ========================== additional tax total ============================== */}
        {showSalesTax && additionalTaxTotal !== 0 && (
          <>
            <RowStack>
              <Typography level="body2">Sales Tax</Typography>
              <Typography level="body2">{toPrice(additionalTaxTotal)}</Typography>
            </RowStack>
            <Divider />
          </>
        )}
        <RowStack>
          <Typography>
            <Typography level="subh2">Total</Typography>
            {!!totalAddonDescription && (
              <Typography level="caption1" sx={{ color: (theme) => theme.palette.brand.charcoal[500] }}>
                ({totalAddonDescription})
              </Typography>
            )}
          </Typography>
          <Typography level="body2">{toPrice(total)}</Typography>
        </RowStack>
        <Divider />
        {/* ========================== amount payable ============================ */}
        {inCheckout && (
          <RowStack>
            <Typography>
              <Typography level="subh2">Amount Payable</Typography>
              {!!totalAddonDescription && (
                <Typography level="caption1" sx={{ color: (theme) => theme.palette.brand.charcoal[500] }}>
                  ({totalAddonDescription})
                </Typography>
              )}
            </Typography>
            <Typography level="body2">{toPrice(payableTotal)}</Typography>
          </RowStack>
        )}
      </Box>
      <NiceModal {...modalProps} showCancelBtn={false} confirmText="Close">
        <PromotionDetails promotions={promotions} />
      </NiceModal>
    </React.Fragment>
  );
};

export default OrderSummary;
